// src/components/DoctorAppointmentSlot.js
// Doctor Appointment Slot Management Component
// Used as a tab inside OPD Registration page
import React, { useEffect, useState, useMemo } from "react";
import AppConst from "../apis/appconst";
import axios from "axios";
import { toast } from "react-toastify";
import Swal from 'sweetalert2';
import Select from "react-select";

function DoctorAppointmentSlot({ parentDoctors }) {
  const [ldata, setLdata] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterDoctor, setFilterDoctor] = useState('');
  const [filterDate, setFilterDate] = useState(()=>{const d=new Date();return`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`});

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [slotForm, setSlotForm] = useState({
    doctor_id:'', start_date:'', end_date:'', start_time:'', end_time:'', consultation_min:'10',
    weekly_off: [],        // ✅ array of weekday numbers [0..6]
    excluded_dates: [],    // ✅ array of "YYYY-MM-DD" strings
  });
  //const [newExcludedDate, setNewExcludedDate] = useState(''); // temp input for adding excluded dates

  const [newExcludedDate, setNewExcludedDate] = useState(''); // temp input for adding excluded dates
  const [excludedFromDate, setExcludedFromDate] = useState(''); // ✅ NEW: range start
  const [excludedToDate, setExcludedToDate] = useState('');     // ✅ NEW: range end

// Weekday labels for UI
const WEEKDAYS = [
  { val: 0, label: 'Sun' },
  { val: 1, label: 'Mon' },
  { val: 2, label: 'Tue' },
  { val: 3, label: 'Wed' },
  { val: 4, label: 'Thu' },
  { val: 5, label: 'Fri' },
  { val: 6, label: 'Sat' },
];

const toggleWeeklyOff = (dayNum) => {
  setSlotForm(p => ({
    ...p,
    weekly_off: p.weekly_off.includes(dayNum)
      ? p.weekly_off.filter(d => d !== dayNum)
      : [...p.weekly_off, dayNum].sort()
  }));
};

const addExcludedDate = () => {
  if (!newExcludedDate) return;
  if (slotForm.excluded_dates.includes(newExcludedDate)) {
    toast.info('Date already excluded');
    return;
  }
  if (slotForm.start_date && newExcludedDate < slotForm.start_date) {
    toast.error('Date is before start date');
    return;
  }
  if (slotForm.end_date && newExcludedDate > slotForm.end_date) {
    toast.error('Date is after end date');
    return;
  }
  setSlotForm(p => ({ ...p, excluded_dates: [...p.excluded_dates, newExcludedDate].sort() }));
  setNewExcludedDate('');
};

// ✅ NEW: Add a range of dates (from → to, inclusive) as excluded
const addExcludedDateRange = () => {
  if (!excludedFromDate || !excludedToDate) {
    toast.error('Select both From and To dates');
    return;
  }
  if (excludedFromDate > excludedToDate) {
    toast.error('From date must be before or same as To date');
    return;
  }
  if (slotForm.start_date && excludedFromDate < slotForm.start_date) {
    toast.error(`From date is before slot start date (${slotForm.start_date})`);
    return;
  }
  if (slotForm.end_date && excludedToDate > slotForm.end_date) {
    toast.error(`To date is after slot end date (${slotForm.end_date})`);
    return;
  }

  // Build all dates in range
  const datesToAdd = [];
  const startD = new Date(excludedFromDate + 'T00:00:00');
  const endD = new Date(excludedToDate + 'T00:00:00');
  for (let d = new Date(startD); d <= endD; d.setDate(d.getDate() + 1)) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    datesToAdd.push(`${y}-${m}-${dd}`);
  }

  // Merge with existing, dedupe
  setSlotForm(p => {
    const merged = Array.from(new Set([...p.excluded_dates, ...datesToAdd])).sort();
    const newlyAdded = merged.length - p.excluded_dates.length;
    if (newlyAdded > 0) {
      toast.success(`Added ${newlyAdded} date${newlyAdded > 1 ? 's' : ''} to excluded list`);
    } else {
      toast.info('All dates in range were already excluded');
    }
    return { ...p, excluded_dates: merged };
  });

  // Reset range inputs
  setExcludedFromDate('');
  setExcludedToDate('');
};

const removeExcludedDate = (dateStr) => {
  setSlotForm(p => ({ ...p, excluded_dates: p.excluded_dates.filter(d => d !== dateStr) }));
};

// ✅ NEW: Clear all excluded dates
const clearAllExcludedDates = async () => {
  if (slotForm.excluded_dates.length === 0) return;
  const r = await Swal.fire({
    title: 'Clear all excluded dates?',
    text: `This will remove ${slotForm.excluded_dates.length} date(s)`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Yes, clear all',
    confirmButtonColor: '#d33'
  });
  if (r.isConfirmed) {
    setSlotForm(p => ({ ...p, excluded_dates: [] }));
  }
};






  // Slot detail modal (view individual time slots)
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailSlot, setDetailSlot] = useState(null);
  const [detailDate, setDetailDate] = useState('');
  const [detailTimes, setDetailTimes] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // Selected doctor info for modal header
  const selectedDoctorInfo = useMemo(()=>{
    if(!slotForm.doctor_id)return null;
    return doctors.find(d=>String(d.user_id)===String(slotForm.doctor_id));
  },[slotForm.doctor_id,doctors]);

  const doctorOptions = useMemo(()=>
    doctors.map(d=>({value:d.user_id,label:`${d.fname||''} ${d.lname||''}`.trim()||d.name||'Doctor'}))
  ,[doctors]);

  // Generated preview slots
  const previewSlots = useMemo(()=>{
    if(!slotForm.start_time||!slotForm.end_time||!slotForm.consultation_min)return[];
    const toMin=(t)=>{const[h,m]=t.split(':').map(Number);return h*60+(m||0)};
    const startM=toMin(slotForm.start_time),endM=toMin(slotForm.end_time);
    const consM=Math.max(1,parseInt(slotForm.consultation_min)||10);
    if(endM<=startM)return[];
    const result=[];let n=1;
    for(let t=startM;t+consM<=endM;t+=consM){
      const h=Math.floor(t/60),m=t%60;
      const h12=h%12||12,ampm=h>=12?'PM':'AM';
      result.push({num:n++,time:`${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`,display:`${h12}:${String(m).padStart(2,'0')} ${ampm}`});
    }
    return result;
  },[slotForm.start_time,slotForm.end_time,slotForm.consultation_min]);

  // Init
  useEffect(()=>{
    const sd=sessionStorage.getItem("ldata");
    if(sd){const p=JSON.parse(sd);setLdata(p);if(String(p?.role_id)==='1'||p?.is_admin===1)setIsAdmin(true)}
    if(parentDoctors&&parentDoctors.length>0){
      console.log("Using parentDoctors, count:", parentDoctors.length);
      setDoctors(parentDoctors);
    } else {
      loadDoctors();
    }
  },[]);

  // Also update if parentDoctors changes later (e.g. parent loads async)
  useEffect(()=>{
    if(parentDoctors&&parentDoctors.length>0){
      console.log("parentDoctors updated, count:", parentDoctors.length);
      setDoctors(parentDoctors);
    }
  },[parentDoctors]);

  useEffect(()=>{fetchSlots()},[filterDoctor,filterDate]);

  const loadDoctors=async()=>{
    try{
      const fd=new FormData();
      fd.append('type','doctor');
      fd.append('page_no','none');
      fd.append('search_key','');
      fd.append('user_cre_by','1');
      fd.append('doc_specality','');
      const res=await axios.post(AppConst.BASE_URL+AppConst.getUserListUrl,fd,{headers:{'Content-Type':'multipart/form-data'}});
      console.log("loadDoctors API response:", res?.data?.status, "count:", res?.data?.records?.length);
      if(res?.data?.status&&Array.isArray(res.data.records)&&res.data.records.length>0){
        setDoctors(res.data.records);
      } else {
        // Fallback: try with user_cre_by=all
        console.log("loadDoctors: trying with user_cre_by=all...");
        const fd2=new FormData();
        fd2.append('type','doctor');
        fd2.append('page_no','none');
        fd2.append('search_key','');
        fd2.append('user_cre_by','all');
        const res2=await axios.post(AppConst.BASE_URL+AppConst.getUserListUrl,fd2,{headers:{'Content-Type':'multipart/form-data'}});
        console.log("loadDoctors fallback response:", res2?.data?.status, "count:", res2?.data?.records?.length);
        if(res2?.data?.status&&Array.isArray(res2.data.records))setDoctors(res2.data.records);
      }
    }catch(e){console.error("loadDoctors error:",e)}
  };

  const fetchSlots=async()=>{
    setLoading(true);
    try{const fd=new FormData();fd.append('action','get_slots');
    if(filterDoctor)fd.append('doctor_id',filterDoctor);
    if(filterDate)fd.append('date',filterDate);
    fd.append('role_id',String(ldata?.role_id||''));fd.append('user_id',String(ldata?.user_id||''));
    const res=await axios.post(AppConst.BASE_URL+(AppConst.doctorSlotUrl||'Webapis/doctor_slot'),fd,{headers:{'Content-Type':'multipart/form-data'}});
    if(res.data?.status)setSlots(res.data.records||[]);else setSlots([])}catch(e){setSlots([])}finally{setLoading(false)}
  };

  const openNewSlot = () => {
    setEditingSlot(null);
    const today = filterDate || new Date().toISOString().split('T')[0];
    setSlotForm({
      doctor_id: filterDoctor || '',
      start_date: today, end_date: today,
      start_time: '', end_time: '', consultation_min: '10',
      weekly_off: [], excluded_dates: []
    });
    setNewExcludedDate('');
    setExcludedFromDate('');  // ✅
    setExcludedToDate('');  
    setShowModal(true);
  };
  
  const openEditSlot = (slot) => {
    setEditingSlot(slot);
    // Parse stored CSVs back into arrays
    const parsedWeeklyOff = (slot.slot_weekly_off || '').toString().split(',').map(x => x.trim()).filter(x => x !== '').map(Number);
    const parsedExcluded = (slot.slot_excluded_dates || '').toString().split(',').map(x => x.trim()).filter(x => x !== '');
  
    setSlotForm({
      doctor_id: slot.slot_doctor_id,
      start_date: slot.slot_start_date,
      end_date: slot.slot_end_date,
      start_time: slot.slot_start_time?.substring(0, 5) || '',
      end_time: slot.slot_end_time?.substring(0, 5) || '',
      consultation_min: String(slot.slot_consultation_min || 10),
      weekly_off: parsedWeeklyOff,
      excluded_dates: parsedExcluded,
    });
    setNewExcludedDate('');
    setShowModal(true);
  };

  const saveSlot = async () => {
    if (!slotForm.doctor_id) { toast.error('Select a doctor'); return; }
    if (!slotForm.start_date || !slotForm.start_time || !slotForm.end_time) { toast.error('Fill all fields'); return; }
    if (previewSlots.length === 0) { toast.error('Invalid time range - no slots generated'); return; }
  
    // ✅ Validate excluded dates are within range
    const invalidDates = (slotForm.excluded_dates || []).filter(d => d < slotForm.start_date || d > slotForm.end_date);
    if (invalidDates.length > 0) {
      toast.error(`Excluded date(s) out of range: ${invalidDates.join(', ')}`);
      return;
    }
  
    try {
      const fd = new FormData();
      fd.append('action', 'save_slot');
      if (editingSlot) fd.append('slot_id', editingSlot.slot_id);
      const doc = doctors.find(d => String(d.user_id) === String(slotForm.doctor_id));
      fd.append('doctor_id', slotForm.doctor_id);
      fd.append('doctor_name', doc ? `${doc.fname || ''} ${doc.lname || ''}`.trim() : '');
      fd.append('start_date', slotForm.start_date);
      fd.append('end_date', slotForm.end_date || slotForm.start_date);
      fd.append('start_time', slotForm.start_time);
      fd.append('end_time', slotForm.end_time);
      fd.append('consultation_min', slotForm.consultation_min);
      fd.append('created_by', String(ldata?.user_id || ''));
      fd.append('weekly_off', (slotForm.weekly_off || []).join(','));           // ✅ NEW
      fd.append('excluded_dates', (slotForm.excluded_dates || []).join(','));   // ✅ NEW
  
      const res = await axios.post(AppConst.BASE_URL + (AppConst.doctorSlotUrl || 'Webapis/doctor_slot'), fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data?.status) { toast.success(editingSlot ? 'Slot updated!' : 'Slot created!'); setShowModal(false); fetchSlots(); }
      else toast.error(res.data?.message || 'Failed');
    } catch (e) { toast.error('Error saving slot'); }
  };

  const deleteSlot=async(slot)=>{
    const r=await Swal.fire({title:`Delete this slot?`,text:`${slot.slot_doctor_name} - ${formatTime12h(slot.slot_start_time)} to ${formatTime12h(slot.slot_end_time)}`,icon:'warning',showCancelButton:true,confirmButtonText:'Delete',confirmButtonColor:'#d33'});
    if(!r.isConfirmed)return;
    try{const fd=new FormData();fd.append('action','delete_slot');fd.append('slot_id',slot.slot_id);fd.append('role_id',String(ldata?.role_id||''));
    const res=await axios.post(AppConst.BASE_URL+(AppConst.doctorSlotUrl||'Webapis/doctor_slot'),fd,{headers:{'Content-Type':'multipart/form-data'}});
    if(res.data?.status){toast.success('Deleted');fetchSlots()}else toast.error(res.data?.message||'Failed')}catch(e){toast.error('Error')}
  };

  const canEditSlot = (slot) => {
    return isAdmin;
  };

  const viewSlotDetail=async(slot)=>{
    setDetailSlot(slot);setDetailDate(filterDate||slot.slot_start_date);setShowDetailModal(true);setDetailLoading(true);
    try{const fd=new FormData();fd.append('action','get_slot_times');fd.append('slot_id',slot.slot_id);fd.append('date',filterDate||slot.slot_start_date);
    const res=await axios.post(AppConst.BASE_URL+(AppConst.doctorSlotUrl||'Webapis/doctor_slot'),fd,{headers:{'Content-Type':'multipart/form-data'}});
    if(res.data?.status)setDetailTimes(res.data.times||[]);else setDetailTimes([])}catch(e){setDetailTimes([])}finally{setDetailLoading(false)}
  };

  const formatTime12h=(t)=>{if(!t)return'';const[h,m]=(t.substring(0,5)).split(':').map(Number);const h12=h%12||12;return`${h12}:${String(m).padStart(2,'0')} ${h>=12?'PM':'AM'}`};
  const formatDate=(d)=>{if(!d)return'';const dt=new Date(d+'T00:00:00');const months=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];return`${dt.getDate()} ${months[dt.getMonth()]} ${dt.getFullYear()}`};

  return(
    <div>
      {/* Filter Bar */}
      <div className="card mb-3"><div className="card-body py-3"><div className="row align-items-end g-3">
        <div className="col-md-3"><label className="form-label">Filter by Doctor</label>
          <Select options={doctorOptions} value={doctorOptions.find(o=>String(o.value)===String(filterDoctor))||null} onChange={sel=>setFilterDoctor(sel?sel.value:'')} placeholder="All Doctors" isClearable isDisabled={!isAdmin} menuPortalTarget={document.body} styles={{menuPortal:b=>({...b,zIndex:9999}),menu:b=>({...b,zIndex:9999})}}/>
        </div>
        <div className="col-md-2"><label className="form-label">Date</label>
          <input type="date" className="form-control" value={filterDate} onChange={e=>setFilterDate(e.target.value)}/>
        </div>
        <div className="col-md-2"><button className="btn btn-primary" onClick={fetchSlots}><i className="ri-refresh-line me-1"></i>Refresh</button></div>
        <div className="col-md-5 text-end"><button className="btn btn-success" onClick={openNewSlot}><i className="ri-add-line me-1"></i>New Slot</button></div>
      </div></div></div>

      {/* Slots Table */}
      <div className="card"><div className="card-body">
        <h5 className="mb-3" style={{color:'#405189'}}><i className="ri-calendar-check-line me-2"></i>Doctor Appointment Slots</h5>
        {loading?<div className="text-center py-4">Loading...</div>:
        slots.length===0?<div className="text-center py-4 text-muted"><i className="ri-calendar-line" style={{fontSize:48}}></i><p className="mt-2">No slots found for the selected filters</p></div>:
        <div className="table-responsive"><table className="table table-bordered table-hover"><thead style={{backgroundColor:'#f1f5fb'}}><tr>
          <th style={{width:50}}>#</th><th>Doctor</th><th>Date Range</th><th>Time</th><th>Per Patient (min)</th><th>Total Slots/Day</th><th>Bookings</th><th style={{width:180}}>Actions</th>
        </tr></thead><tbody>
          {slots.map((s,i)=>{const ce=canEditSlot(s);const hasBooking=s.booking_count>0;return(
            <tr key={s.slot_id}>
              <td>{i+1}</td>
              <td><strong>{s.slot_doctor_name||'Doctor'}</strong></td>
              <td>
  {formatDate(s.slot_start_date)}
  {s.slot_start_date !== s.slot_end_date && <span> → {formatDate(s.slot_end_date)}</span>}
  {s.slot_weekly_off && (
    <div>
      <small className="text-danger" style={{fontSize:11}}>
        <i className="ri-calendar-close-line"/> Off: {
          s.slot_weekly_off.split(',').filter(x=>x!=='').map(v => WEEKDAYS.find(w => w.val === Number(v))?.label).join(', ')
        }
      </small>
    </div>
  )}
  {s.slot_excluded_dates && s.slot_excluded_dates.length > 0 && (
    <div>
      <small className="text-muted" style={{fontSize:11}}>
        <i className="ri-calendar-event-line"/> Excluded: {
          s.slot_excluded_dates.split(',').filter(x=>x!=='').length
        } date(s)
      </small>
    </div>
  )}
</td>
              <td><span className="badge bg-primary px-2 py-1">{formatTime12h(s.slot_start_time)}</span> — <span className="badge bg-primary px-2 py-1">{formatTime12h(s.slot_end_time)}</span></td>
              <td className="text-center"><strong>{s.slot_consultation_min} min</strong></td>
              <td className="text-center"><span className="badge bg-info">{s.total_slots_per_day}</span></td>
              <td className="text-center">{hasBooking?<span className="badge bg-warning text-dark">{s.booking_count} Booked</span>:<span className="badge bg-light text-muted">None</span>}</td>
              <td>
                <button className="btn btn-sm btn-outline-info me-1" onClick={()=>viewSlotDetail(s)} title="View Slots"><i className="ri-eye-line"></i></button>
                {ce && <button className="btn btn-sm btn-outline-warning me-1" onClick={()=>openEditSlot(s)} title="Edit"><i className="ri-pencil-line"></i></button>}
                
                
                {ce&&<button className="btn btn-sm btn-outline-danger" onClick={()=>deleteSlot(s)} title="Delete" disabled={hasBooking&&!isAdmin}><i className="ri-delete-bin-line"></i></button>}
              </td>
            </tr>
          )})}
        </tbody></table></div>}
      </div></div>

      {/* CREATE/EDIT SLOT MODAL */}
      {showModal&&(<div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:99999}}>
        <div style={{background:'#fff',borderRadius:12,width:'95%',maxWidth:700,maxHeight:'90vh',overflow:'auto',boxShadow:'0 10px 40px rgba(0,0,0,0.2)'}}>
          {/* Modal Header */}
          <div style={{background:'linear-gradient(135deg,#405189,#6366f1)',color:'#fff',padding:'16px 20px',borderRadius:'12px 12px 0 0'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <h5 style={{margin:0}}><i className={`${editingSlot?'ri-pencil-line':'ri-add-circle-line'} me-2`}></i>{editingSlot?'Edit Slot':'New Appointment Slot'}</h5>
              <button onClick={()=>setShowModal(false)} style={{background:'none',border:'none',color:'#fff',fontSize:24,cursor:'pointer'}}>&times;</button>
            </div>
          </div>

          <div style={{padding:20}}>
            {/* Doctor Details Header */}
            <div style={{background:'#f1f5fb',borderRadius:8,padding:16,marginBottom:20}}>
              <h6 style={{color:'#405189',marginBottom:12}}><i className="ri-user-star-line me-2"></i>Doctor Details</h6>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-bold">Select Doctor *</label>
                  <Select options={doctorOptions} value={doctorOptions.find(o=>String(o.value)===String(slotForm.doctor_id))||null}
                    onChange={sel=>setSlotForm(p=>({...p,doctor_id:sel?sel.value:''}))} placeholder="Select doctor..." isClearable
                    menuPortalTarget={document.body} menuPosition="fixed"
                    styles={{menuPortal:b=>({...b,zIndex:999999}),menu:b=>({...b,zIndex:999999}),control:b=>({...b,minHeight:38})}}/>
                </div>
                {selectedDoctorInfo&&(<>
                  <div className="col-md-3"><label className="form-label text-muted">Name</label><p className="fw-bold mb-0">{`${selectedDoctorInfo.fname||''} ${selectedDoctorInfo.lname||''}`.trim()}</p></div>
                  <div className="col-md-3"><label className="form-label text-muted">Reg No</label><p className="fw-bold mb-0">{selectedDoctorInfo.doc?.doc_reg_number||selectedDoctorInfo.doc?.doc_reg_no||'N/A'}</p></div>
                  <div className="col-md-3"><label className="form-label text-muted">Designation</label><p className="mb-0">{selectedDoctorInfo.doc?.doc_designation||selectedDoctorInfo.doc?.doc_degree||'N/A'}</p></div>
                  <div className="col-md-3"><label className="form-label text-muted">Speciality</label><p className="mb-0">{selectedDoctorInfo.doc?.doc_specality||'N/A'}</p></div>
                  <div className="col-md-3"><label className="form-label text-muted">OPD Fees</label><p className="fw-bold mb-0" style={{color:'#198754'}}>₹{selectedDoctorInfo.doc?.doc_fees||'0'}</p></div>
                </>)}
              </div>
            </div>

            {/* Slot Details */}
            <h6 style={{color:'#405189',marginBottom:12}}><i className="ri-time-line me-2"></i>Slot Details</h6>
            <div className="row g-3 mb-3">
              <div className="col-md-6"><label className="form-label fw-bold">Start Date *</label><input type="date" className="form-control" value={slotForm.start_date} onChange={e=>setSlotForm(p=>({...p,start_date:e.target.value}))}/></div>
              <div className="col-md-6"><label className="form-label fw-bold">End Date</label><input type="date" className="form-control" value={slotForm.end_date} onChange={e=>setSlotForm(p=>({...p,end_date:e.target.value}))} min={slotForm.start_date}/><small className="text-muted">Same as start date for single day</small></div>
              <div className="col-md-4"><label className="form-label fw-bold">Start Time *</label><input type="time" className="form-control" value={slotForm.start_time} onChange={e=>setSlotForm(p=>({...p,start_time:e.target.value}))}/></div>
              <div className="col-md-4"><label className="form-label fw-bold">End Time *</label><input type="time" className="form-control" value={slotForm.end_time} onChange={e=>setSlotForm(p=>({...p,end_time:e.target.value}))} min={slotForm.start_time}/></div>
              <div className="col-md-4"><label className="form-label fw-bold">Consultation (min) *</label><input type="number" className="form-control" min="1" max="120" value={slotForm.consultation_min} onChange={e=>setSlotForm(p=>({...p,consultation_min:e.target.value}))}/></div>
           
           
           
           {/* ✅ Weekly Off Days */}
<div style={{background:'#fff8e1',borderRadius:8,padding:14,marginBottom:14,border:'1px solid #ffe082'}}>
  <h6 style={{color:'#e65100',marginBottom:10}}>
    <i className="ri-calendar-close-line me-2"></i>Weekly Off Days
    <small className="text-muted ms-2" style={{fontSize:12,fontWeight:400}}>
      (Click days when doctor is unavailable — applied across entire range)
    </small>
  </h6>
  <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
    {WEEKDAYS.map(wd => {
      const active = slotForm.weekly_off.includes(wd.val);
      return (
        <button
          key={wd.val}
          type="button"
          onClick={() => toggleWeeklyOff(wd.val)}
          className={`btn btn-sm ${active ? 'btn-danger' : 'btn-outline-secondary'}`}
          style={{minWidth:55}}
        >
          {active && <i className="ri-close-circle-line me-1"/>}
          {wd.label}
        </button>
      );
    })}
  </div>
  {slotForm.weekly_off.length > 0 && (
    <small className="text-danger d-block mt-2">
      <i className="ri-information-line me-1"/>
      Doctor will be unavailable on: {slotForm.weekly_off.map(v => WEEKDAYS.find(w => w.val === v)?.label).join(', ')} (every week)
    </small>
  )}
</div>

{/* ✅ Specific Excluded Dates */}
{/* ✅ Specific Excluded Dates — Single + Range */}
<div style={{background:'#fce4ec',borderRadius:8,padding:14,marginBottom:14,border:'1px solid #f8bbd0'}}>
  <h6 style={{color:'#c2185b',marginBottom:10}}>
    <i className="ri-calendar-event-line me-2"></i>Specific Unavailable Dates
    <small className="text-muted ms-2" style={{fontSize:12,fontWeight:400}}>
      (Add single dates or a date range when doctor won't be available)
    </small>
  </h6>

  {/* Single Date Add */}
  <div className="mb-2">
    <label className="form-label mb-1" style={{fontSize:12,fontWeight:600,color:'#c2185b'}}>
      Add Single Date:
    </label>
    <div className="d-flex gap-2 align-items-center">
      <input
        type="date"
        className="form-control form-control-sm"
        value={newExcludedDate}
        min={slotForm.start_date}
        max={slotForm.end_date}
        onChange={e => setNewExcludedDate(e.target.value)}
        style={{maxWidth:180}}
      />
      <button type="button" className="btn btn-outline-danger btn-sm" onClick={addExcludedDate} disabled={!newExcludedDate}>
        <i className="ri-add-line me-1"/>Add
      </button>
    </div>
  </div>

  {/* ✅ NEW: Date Range Add */}
  <div className="mb-2" style={{borderTop:'1px dashed #f48fb1',paddingTop:10,marginTop:10}}>
    <label className="form-label mb-1" style={{fontSize:12,fontWeight:600,color:'#c2185b'}}>
      Add Date Range (Leaves / Holidays):
    </label>
    <div className="d-flex gap-2 align-items-center flex-wrap">
      <div>
        <small className="text-muted d-block" style={{fontSize:10}}>From</small>
        <input
          type="date"
          className="form-control form-control-sm"
          value={excludedFromDate}
          min={slotForm.start_date}
          max={slotForm.end_date}
          onChange={e => setExcludedFromDate(e.target.value)}
          style={{maxWidth:170}}
        />
      </div>
      <div>
        <small className="text-muted d-block" style={{fontSize:10}}>To</small>
        <input
          type="date"
          className="form-control form-control-sm"
          value={excludedToDate}
          min={excludedFromDate || slotForm.start_date}
          max={slotForm.end_date}
          onChange={e => setExcludedToDate(e.target.value)}
          style={{maxWidth:170}}
        />
      </div>
      <div style={{alignSelf:'flex-end'}}>
        <button
          type="button"
          className="btn btn-danger btn-sm"
          onClick={addExcludedDateRange}
          disabled={!excludedFromDate || !excludedToDate}
        >
          <i className="ri-calendar-close-line me-1"/>Add Range
        </button>
      </div>
    </div>
    {excludedFromDate && excludedToDate && excludedFromDate <= excludedToDate && (
      <small className="text-muted d-block mt-1">
        <i className="ri-information-line me-1"/>
        Will add {(() => {
          const s = new Date(excludedFromDate + 'T00:00:00');
          const e = new Date(excludedToDate + 'T00:00:00');
          return Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1;
        })()} date(s) to excluded list
      </small>
    )}
  </div>

  {/* Excluded Dates Display */}
  <div style={{borderTop:'1px dashed #f48fb1',paddingTop:10,marginTop:10}}>
    <div className="d-flex justify-content-between align-items-center mb-2">
      <label className="form-label mb-0" style={{fontSize:12,fontWeight:600,color:'#c2185b'}}>
        Excluded Dates ({slotForm.excluded_dates.length}):
      </label>
      {slotForm.excluded_dates.length > 0 && (
        <button type="button" className="btn btn-sm btn-outline-secondary" style={{fontSize:11,padding:'2px 8px'}} onClick={clearAllExcludedDates}>
          <i className="ri-delete-bin-line me-1"/>Clear All
        </button>
      )}
    </div>
    {slotForm.excluded_dates.length === 0 ? (
      <small className="text-muted">No specific dates excluded</small>
    ) : (
      <div style={{display:'flex',gap:6,flexWrap:'wrap',maxHeight:150,overflowY:'auto'}}>
        {slotForm.excluded_dates.map(d => (
          <span
            key={d}
            className="badge"
            style={{background:'#fff',color:'#c2185b',border:'1px solid #f48fb1',padding:'6px 10px',fontSize:12,display:'flex',alignItems:'center',gap:4}}
          >
            {formatDate(d)}
            <button
              type="button"
              onClick={() => removeExcludedDate(d)}
              style={{background:'none',border:'none',color:'#c2185b',cursor:'pointer',padding:0,marginLeft:4,fontSize:14,lineHeight:1}}
              title="Remove"
            >
              ×
            </button>
          </span>
        ))}
      </div>
    )}
  </div>
</div>
           
            </div>

            {/* Preview Generated Slots */}
            {previewSlots.length>0&&(<div style={{background:'#e8f5e9',borderRadius:8,padding:16,marginBottom:16}}>
              <h6 style={{color:'#2e7d32',marginBottom:10}}><i className="ri-check-double-line me-2"></i>Generated Slots ({previewSlots.length})</h6>
              <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
                {previewSlots.map(s=>(
                  <span key={s.num} className="badge" style={{background:'#fff',color:'#2e7d32',border:'1px solid #a5d6a7',padding:'6px 12px',fontSize:13,fontWeight:500}}>
                    {s.num}. {s.display}
                  </span>
                ))}
              </div>
            </div>)}
            {slotForm.start_time&&slotForm.end_time&&previewSlots.length===0&&(
              <div className="alert alert-warning py-2"><i className="ri-error-warning-line me-2"></i>No slots can be generated. Check time range and consultation duration.</div>
            )}

            <div className="d-flex justify-content-end gap-2 mt-3">
              <button className="btn btn-secondary" onClick={()=>setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveSlot} disabled={previewSlots.length===0||!slotForm.doctor_id}>
                <i className={`${editingSlot?'ri-save-line':'ri-add-line'} me-1`}></i>{editingSlot?'Update Slot':'Create Slot'}
              </button>
            </div>
          </div>
        </div>
      </div>)}

      {/* SLOT DETAIL/VIEW MODAL */}
      {showDetailModal&&detailSlot&&(<div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:99999}}>
        <div style={{background:'#fff',borderRadius:12,width:'90%',maxWidth:600,maxHeight:'80vh',overflow:'hidden',boxShadow:'0 10px 40px rgba(0,0,0,0.2)'}}>
          <div style={{background:'linear-gradient(135deg,#405189,#6366f1)',color:'#fff',padding:'14px 20px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <h5 style={{margin:0}}><i className="ri-calendar-check-line me-2"></i>{detailSlot.slot_doctor_name} — {formatDate(detailDate)}</h5>
            <button onClick={()=>setShowDetailModal(false)} style={{background:'none',border:'none',color:'#fff',fontSize:24,cursor:'pointer'}}>&times;</button>
          </div>
          <div style={{padding:20,maxHeight:'calc(80vh - 60px)',overflowY:'auto'}}>
            <div className="mb-3 text-muted">{formatTime12h(detailSlot.slot_start_time)} — {formatTime12h(detailSlot.slot_end_time)} | {detailSlot.slot_consultation_min} min per patient</div>
            {detailLoading?<div className="text-center py-4">Loading...</div>:
            detailTimes.length===0?<div className="text-center py-4 text-muted">No slots available</div>:
            <div className="table-responsive"><table className="table table-bordered table-sm"><thead className="table-light"><tr><th style={{width:50}}>#</th><th>Time</th><th>Status</th><th>Patient</th></tr></thead><tbody>
              {detailTimes.map((t,i)=>(
                <tr key={i} style={{background:t.status==='booked'?'#fff3e0':'#fff'}}>
                  <td>{t.slot_number}</td>
                  <td><strong>{t.time_display}</strong> — {t.end_time_display}</td>
                  <td>{t.status==='booked'?<span className="badge bg-warning text-dark">Booked</span>:<span className="badge bg-success">Available</span>}</td>
                  <td>{t.booking?`${t.booking.booking_patient_name||''} (${t.booking.booking_patient_uhid||''})`:<span className="text-muted">—</span>}</td>
                </tr>
              ))}
            </tbody></table></div>}
          </div>
        </div>
      </div>)}
    </div>
  );
}

export default DoctorAppointmentSlot;